import {useLocation} from 'react-router-dom'
import {ThemeToggle} from '@/components/theme-toggle'
import {BrandThemeToggle} from '@/components/brand-theme-toggle'
import {LanguageSwitcher} from '@/components/language-switcher'
import {TimeSwitch} from '@/components/time-switch'
import {HelpToggleButton} from '@/components/help/HelpToggleButton'
import {IdentitySelector} from '@/components/identity-selector'
import {NotificationBell} from '@/features/notifications/NotificationBell'
import {Logo} from '@/components/layout/Logo'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {permissions} from '@/lib/permissions'
import {routes} from '@/lib/routes'

const PAGE_SECTIONS: ReadonlyArray<[string, string]> = [
    [routes.my.root(), 'my-iam'],
    [routes.admin.root(), 'admin'],
    [routes.connectors.root(), 'connectors'],
    [routes.schema.root(), 'schema'],
    [routes.identityManagement.root(), 'identity-management'],
    [routes.systems.root(), 'systems'],
    [routes.accessGovernance.root(), 'access-governance'],
    [routes.effectiveAccess.root(), 'effective-access'],
    [`/${routes.provisioning.segment}`, 'access-provisioning'],
    [routes.backgroundJobs.root(), 'background-jobs'],
    [routes.auditLog.path(), 'audit-log'],
    [routes.reporting.root(), 'reporting'],
] as const

function getPageSuffix(pathname: string): string | undefined {
    return PAGE_SECTIONS.find(([prefix]) => pathname.startsWith(prefix))?.[1]
}

export function Header() {
    const location = useLocation()
    const suffix = getPageSuffix(location.pathname)
    const {hasPermission} = useAuth()
    const i18n = useI18n()

    return (
        <header
            className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="container mx-auto px-4 flex h-14 items-center">
                <div className="mr-4 flex">
                    <Logo suffix={suffix}/>
                </div>

                {/* WHY: wrap the header utility buttons in a navigation landmark
                    with an accessible name so screen-reader users can jump past
                    them with quick-nav 'R' or pull them up via the rotor. Before
                    this, every quick-nav 'B' hit cycled through Logout, Time
                    switch, Notifications, etc. before reaching page buttons.
                    Issue 794. */}
                <nav
                    aria-label={i18n.common.utilityMenuLandmark}
                    data-testid="header-utility-nav"
                    className="flex flex-1 items-center justify-end space-x-2"
                >
                    <IdentitySelector/>
                    {hasPermission(permissions.notifications.read) && <NotificationBell/>}
                    <TimeSwitch/>
                    <HelpToggleButton/>
                    <LanguageSwitcher/>
                    <BrandThemeToggle/>
                    <ThemeToggle/>
                </nav>
            </div>
        </header>
    )
}
